/**
 * Utility functions for detecting changes in form data
 * Can be reused across different pages with forms
 */

/**
 * Helper function to compare arrays for equality
 * @param {Array} arr1 - First array
 * @param {Array} arr2 - Second array
 * @returns {boolean} - True if arrays are equal
 */
const arraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  
  // Sort arrays for comparison (order doesn't matter for groups)
  // Coerce values to strings to avoid type mismatches (e.g., '1' vs 1)
  const sorted1 = [...arr1].map(v => String(v)).sort();
  const sorted2 = [...arr2].map(v => String(v)).sort();
  
  return sorted1.every((val, index) => val === sorted2[index]);
};

/**
 * Detects if there are any changes between current form data and original data
 * @param {Object} currentData - Current form data
 * @param {Object} originalData - Original data to compare against
 * @param {Array} fieldsToCheck - Array of field names to check for changes
 * @returns {Object} - { hasChanges: boolean, changedFields: Array, changeDetails: Object }
 */
export const detectChanges = (currentData, originalData, fieldsToCheck) => {
  if (!currentData || !originalData) {
    return {
      hasChanges: false,
      changedFields: [],
      changeDetails: {},
      error: 'Missing data for comparison'
    };
  }

  const changedFields = [];
  const changeDetails = {};

  // Check each specified field for changes
  fieldsToCheck.forEach(field => {
    const currentValue = currentData[field];
    const originalValue = originalData[field];

    // Handle different data types
    let hasChanged = false;

    // Special handling for arrays (like groups)
    if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
      hasChanged = !arraysEqual(currentValue, originalValue);
    } else {
      hasChanged = currentValue !== originalValue;
    }

    if (hasChanged) {
      changedFields.push(field);
      changeDetails[field] = {
        original: originalValue,
        current: currentValue
      };
    }
  });

  const hasChanges = changedFields.length > 0;

  return {
    hasChanges,
    changedFields,
    changeDetails,
    summary: hasChanges 
      ? `${changedFields.length} field(s) changed: ${changedFields.join(', ')}`
      : 'No changes detected'
  };
};

/**
 * Creates a standardized "No Changes" modal data object
 * @param {string} entityName - Name of the entity being updated (e.g., "profile", "user", "group")
 * @param {string} customMessage - Optional custom message
 * @returns {Object} - Modal data object for success modal
 */
export const createNoChangesModalData = (entityName = 'item', customMessage = null) => {
  return {
    title: "No Changes Detected",
    message: customMessage || `No changes were made to your ${entityName}.`,
    details: "The current values match the original data."
  };
};

/**
 * Creates a standardized "Changes Detected" modal data object
 * @param {Object} changeResult - Result from detectChanges function
 * @param {string} entityName - Name of the entity being updated
 * @returns {Object} - Modal data object for info modal
 */
export const createChangesDetectedModalData = (changeResult, entityName = 'item') => {
  return {
    title: "Changes Detected",
    message: `${changeResult.changedFields.length} field(s) will be updated in your ${entityName}.`,
    details: changeResult.changeDetails
  };
};

/**
 * Logs change detection results for debugging
 * @param {string} context - Context for the log (e.g., "Profile update", "User edit")
 * @param {Object} changeResult - Result from detectChanges function
 * @param {Object} originalData - Original data (for logging)
 * @param {Object} currentData - Current data (for logging)
 */
export const logChangeDetection = (context, changeResult, originalData, currentData) => {
  console.log(`${context} - change detection:`, {
    original: originalData,
    current: currentData,
    result: changeResult
  });
};

/**
 * Common field configurations for different entity types
 */
export const FIELD_CONFIGS = {
  profile: ['firstName', 'lastName'],
  user: ['firstName', 'lastName', 'email'],
  group: ['name', 'description'],
  // Add more configurations as needed
};

/**
 * Convenience function for common change detection patterns
 * @param {string} entityType - Type of entity (profile, user, group, etc.)
 * @param {Object} currentData - Current form data
 * @param {Object} originalData - Original data
 * @returns {Object} - Change detection result
 */
export const detectChangesForEntity = (entityType, currentData, originalData) => {
  const fieldsToCheck = FIELD_CONFIGS[entityType] || Object.keys(currentData);
  return detectChanges(currentData, originalData, fieldsToCheck);
};

/**
 * Advanced change detection for multi-table entities (like users with groups)
 * @param {Object} currentData - Current form data
 * @param {Object} originalData - Original data
 * @param {Object} config - Configuration for different table updates
 * @returns {Object} - Detailed change detection result with update strategies
 */
export const detectMultiTableChanges = (currentData, originalData, config) => {
  const result = {
    hasAnyChanges: false,
    updateStrategies: {},
    changeSummary: {},
    noChangesDetected: true
  };

  // Check each table/entity for changes
  Object.keys(config).forEach(tableName => {
    const tableConfig = config[tableName];
    const tableResult = detectChanges(
      currentData[tableConfig.dataKey], 
      originalData[tableConfig.dataKey], 
      tableConfig.fieldsToCheck
    );

    result.updateStrategies[tableName] = {
      needsUpdate: tableResult.hasChanges,
      fields: tableResult.changedFields,
      changes: tableResult.changeDetails,
      updateData: tableResult.hasChanges ? 
        extractUpdateData(currentData[tableConfig.dataKey], tableResult.changedFields) : null
    };

    if (tableResult.hasChanges) {
      result.hasAnyChanges = true;
      result.noChangesDetected = false;
      result.changeSummary[tableName] = tableResult.summary;
    }
  });

  return result;
};

/**
 * Extracts only the changed fields for update
 * @param {Object} data - Full data object
 * @param {Array} changedFields - Array of field names that changed
 * @returns {Object} - Object containing only changed fields
 */
export const extractUpdateData = (data, changedFields) => {
  const updateData = {};
  changedFields.forEach(field => {
    updateData[field] = data[field];
  });
  return updateData;
};

/**
 * Creates modal data for multi-table change detection
 * @param {Object} multiTableResult - Result from detectMultiTableChanges
 * @param {string} entityName - Name of the entity being updated
 * @returns {Object} - Modal data object
 */
export const createMultiTableModalData = (multiTableResult, entityName = 'item') => {
  if (multiTableResult.noChangesDetected) {
    return createNoChangesModalData(entityName);
  }

  const changedTables = Object.keys(multiTableResult.changeSummary);
  const totalChanges = changedTables.reduce((sum, table) => {
    return sum + multiTableResult.updateStrategies[table].fields.length;
  }, 0);

  return {
    title: "Changes Detected",
    message: `${totalChanges} field(s) will be updated across ${changedTables.length} table(s) in your ${entityName}.`,
    details: {
      "Tables to Update": changedTables.join(', '),
      "Change Summary": multiTableResult.changeSummary,
      "Update Strategy": multiTableResult.updateStrategies
    }
  };
};

/**
 * Configuration for multi-table entities
 */
export const MULTI_TABLE_CONFIGS = {
  user: {
    users: {
      dataKey: 'userData',
      fieldsToCheck: ['firstName', 'lastName', 'email']
    },
    user_groups: {
      dataKey: 'groups',
      fieldsToCheck: ['groups'], // Special handling for array comparison
      isArrayField: true
    }
  },
  group: {
    groups: {
      dataKey: 'groupData',
      fieldsToCheck: ['name', 'description']
    },
    user_groups: {
      dataKey: 'users',
      fieldsToCheck: ['users'],
      isArrayField: true
    }
  }
};
